import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { TrendingUp } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cardStyles } from "@/lib/status-styles";
import { formatNumber } from "@/lib/formatting-utils.js";

/**
 * Monthly Availability Table Component
 * Displays 12-month availability data with row selection
 * @param {Object} props
 * @param {Array} props.monthlyAvailability - Array of monthly availability data
 * @param {Object|null} props.summaryTotals - Summary totals for 12 months
 * @param {string|null} props.selectedMonth - Currently selected month
 * @param {Function} props.onMonthSelect - Callback when a month is selected
 * @param {boolean} props.isLoading - Loading state
 * @param {string|null} props.error - Error message
 * @returns {JSX.Element} Monthly Availability Table component
 */
export default function MonthlyAvailabilityTable({
  monthlyAvailability,
  summaryTotals,
  selectedMonth,
  onMonthSelect,
  isLoading,
  error,
}) {
  const THRESHOLD = 95;

  return (
    <Card className='bg-card border border-border shadow-sm rounded-lg overflow-hidden'>
      <CardHeader className='py-3 px-4 border-b border-border'>
        <div className='flex items-center gap-2'>
          <TrendingUp className='h-5 w-5 text-muted-foreground' />
          <CardTitle className='text-base font-medium text-foreground'>
            Monthly Availability
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className={cardStyles.content}>
        {isLoading ? (
          <div className='flex items-center justify-center py-8'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
            <span className='ml-3 text-muted-foreground'>
              Loading monthly availability...
            </span>
          </div>
        ) : error ? (
          <div className='flex items-center justify-center py-8'>
            <div className='text-center'>
              <p className='text-red-600 mb-2'>{error}</p>
            </div>
          </div>
        ) : monthlyAvailability.length === 0 ? (
          <div className='flex items-center justify-center py-8'>
            <div className='text-center'>
              <p className='text-muted-foreground mb-2'>
                No monthly availability data
              </p>
            </div>
          </div>
        ) : (
          <div className={cardStyles.tableContainer}>
            <Table className='w-full'>
              <TableHeader className={cardStyles.tableHeader}>
                {/* Summary Row */}
                {summaryTotals && (
                  <TableRow className='bg-muted border-b-2 border-border font-semibold'>
                    <TableHead className='font-semibold text-foreground py-1.5 px-4 bg-muted'>
                      12-Month totals
                    </TableHead>
                    <TableHead
                      className={`font-semibold py-1.5 px-4 bg-muted text-right ${
                        summaryTotals.runtimePercentage < THRESHOLD
                          ? "text-amber-500"
                          : "text-green-600"
                      }`}>
                      <span className='text-sm font-mono font-semibold'>
                        {formatNumber(summaryTotals.runtimePercentage, 1)}%
                      </span>
                    </TableHead>
                    <TableHead
                      className={`font-semibold py-1.5 px-4 bg-muted text-right ${
                        summaryTotals.mechanicalAvailabilityPercentage
                        < THRESHOLD
                          ? "text-amber-500"
                          : "text-green-600"
                      }`}>
                      <span className='text-sm font-mono font-semibold'>
                        {formatNumber(
                          summaryTotals.mechanicalAvailabilityPercentage,
                          1
                        )}
                        %
                      </span>
                    </TableHead>
                    <TableHead className='font-semibold text-foreground py-1.5 px-4 bg-muted text-right'>
                      <span className='text-sm font-mono font-semibold'>
                        {formatNumber(summaryTotals.totalDowntimeHours, 2)} h
                      </span>
                    </TableHead>
                    <TableHead className='font-semibold text-foreground py-1.5 px-4 bg-muted text-right'>
                      <span className='text-sm font-mono font-medium'>
                        {summaryTotals.totalEventCount}
                      </span>
                    </TableHead>
                  </TableRow>
                )}
                {/* Column Headers */}
                <TableRow className={cardStyles.tableRow}>
                  <TableHead className={`${cardStyles.tableHead} py-1.5`}>
                    Month
                  </TableHead>
                  <TableHead
                    className={`${cardStyles.tableHead} text-right py-1.5`}>
                    Runtime
                  </TableHead>
                  <TableHead
                    className={`${cardStyles.tableHead} text-right py-1.5`}>
                    Mechanical Availability
                  </TableHead>
                  <TableHead
                    className={`${cardStyles.tableHead} text-right py-1.5`}>
                    Downtime
                  </TableHead>
                  <TableHead
                    className={`${cardStyles.tableHead} text-right py-1.5`}>
                    Events
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyAvailability.map((month, index) => {
                  const isSelected = selectedMonth === month.month;
                  return (
                    <TableRow
                      key={month.month}
                      className={
                        isSelected
                          ? "border-b border-border bg-primary/10 hover:bg-primary/15 transition-colors cursor-pointer"
                          : `${cardStyles.bodyRow} cursor-pointer hover:bg-primary/5`
                      }
                      onClick={() => {
                        onMonthSelect(isSelected ? null : month.month);
                      }}>
                      <TableCell className={cardStyles.tableCell}>
                        <div className='flex items-center gap-2'>
                          {isSelected && (
                            <div className='w-2 h-2 rounded-full bg-primary'></div>
                          )}
                          <span className='text-sm font-mono font-light'>
                            {month.month}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell
                        className={`font-light py-1.5 px-4 text-right ${
                          month.hasData && month.runtimePercentage < THRESHOLD
                            ? "text-amber-500"
                            : "text-green-600"
                        }`}>
                        {month.hasData ? (
                          <span className='text-sm font-mono font-light'>
                            {formatNumber(month.runtimePercentage, 1)}%
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell
                        className={`font-light py-1.5 px-4 text-right ${
                          month.hasData
                          && month.mechanicalAvailabilityPercentage < THRESHOLD
                            ? "text-amber-500"
                            : "text-green-600"
                        }`}>
                        {month.hasData ? (
                          <span className='text-sm font-mono font-light'>
                            {formatNumber(
                              month.mechanicalAvailabilityPercentage,
                              1
                            )}
                            %
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className='text-sm text-foreground font-normal py-3 px-4 text-right'>
                        {month.hasData ? (
                          <span className='text-sm font-mono font-light'>
                            {formatNumber(month.downtimeHours, 2)} h
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className='text-sm text-foreground font-normal py-3 px-4 text-right'>
                        {month.hasData ? (
                          <span className='text-sm font-mono font-light'>
                            {month.eventCount}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
